import { supabase } from '../supabaseClient';
import {
  mergeProgramLearningContentFields,
  parseProgramLearningContentFieldsFromEditor,
  readProgramLearningContentFields,
  serializeProgramLearningContentFieldsForEditor,
  type ProgramLearningContentFields,
  type ProgramLearningContentRowLike,
} from './programLearningContentFields';

export type ProgramLearningTextOwnerPatchKind = 'program_cell' | 'fixed_row' | 'scoped_row';

type LearningContentBaseline = ProgramLearningContentRowLike | ProgramLearningContentFields | null | undefined;

function isClearedLearningFields(fields: ProgramLearningContentFields): boolean {
  return (
    !fields.body.trim() &&
    !fields.note.trim() &&
    !fields.presentationRefId &&
    !fields.worksheetRefId
  );
}

function cellRowHadLearningBody(baselineRow?: LearningContentBaseline): boolean {
  if (!baselineRow) return false;
  if ('body' in baselineRow && typeof (baselineRow as ProgramLearningContentFields).body === 'string') {
    const fields = baselineRow as ProgramLearningContentFields;
    return !!(fields.body.trim() || fields.note.trim());
  }
  const fields = readProgramLearningContentFields(baselineRow as ProgramLearningContentRowLike);
  return !!(fields.body.trim() || fields.note.trim());
}

function readBaselineFields(
  baselineRaw: string | null | undefined,
  baselineRow?: LearningContentBaseline
): ProgramLearningContentFields {
  /**
   * 표 draft를 넘긴 경우(빈 문자열 포함)가 화면 권위.
   * undefined/null 만 DB row로 폴백 — 빈 칸을 옛 본문으로 채우지 않는다.
   */
  if (baselineRaw !== undefined && baselineRaw !== null) {
    return parseProgramLearningContentFieldsFromEditor(baselineRaw);
  }
  if (baselineRow && 'body' in baselineRow && typeof baselineRow.body === 'string') {
    return baselineRow;
  }
  if (baselineRow) return readProgramLearningContentFields(baselineRow as ProgramLearningContentRowLike);
  return parseProgramLearningContentFieldsFromEditor(baselineRaw);
}

const LEARNING_CONTENT_SELECT =
  'learning_text, learning_note, presentation_ref_id, worksheet_ref_id';

/**
 * 슬라이드·활동지 flush: 화면(표 draft) 본문·메모는 유지하고, patch는 ref만 보탠다.
 * 표에서 칸을 비운 상태(빈 draft)는 DB 본문으로 되살리지 않는다.
 * 표 draft를 안 넘긴 경우(null)만 DB row 본문을 쓴다.
 */
export function mergeLearningTextOwnerPatchWithSavedBaseline(
  patchRaw: string | null | undefined,
  baselineRaw: string | null | undefined,
  baselineRow?: LearningContentBaseline
): string {
  const tableDraftProvided = baselineRaw !== undefined && baselineRaw !== null;
  const baselineFields = readBaselineFields(baselineRaw, baselineRow);
  if (tableDraftProvided && isClearedLearningFields(baselineFields)) {
    if (cellRowHadLearningBody(baselineRow)) {
      return '';
    }
    const patchFields = parseProgramLearningContentFieldsFromEditor(patchRaw);
    return serializeProgramLearningContentFieldsForEditor({
      body: '',
      note: '',
      presentationRefId: patchFields.presentationRefId,
      worksheetRefId: patchFields.worksheetRefId,
    });
  }
  const patchFields = parseProgramLearningContentFieldsFromEditor(patchRaw);
  if (!(patchRaw ?? '').trim()) {
    return serializeProgramLearningContentFieldsForEditor(baselineFields);
  }
  const merged = mergeProgramLearningContentFields(baselineFields, {
    body: baselineFields.body,
    note: baselineFields.note,
    presentationRefId: patchFields.presentationRefId || baselineFields.presentationRefId,
    worksheetRefId: patchFields.worksheetRefId || baselineFields.worksheetRefId,
  });
  return serializeProgramLearningContentFieldsForEditor(merged);
}

/** patch가 본문·ref만 갱신하고 note를 생략한 경우 baseline 메모 유지 */
export function mergeLearningTextStructuredFieldsFromBaseline(
  patchRaw: string | null | undefined,
  baselineRaw: string | null | undefined,
  baselineRow?: LearningContentBaseline
): string {
  const patchFields = parseProgramLearningContentFieldsFromEditor(patchRaw);
  const baselineFields = readBaselineFields(baselineRaw, baselineRow);
  if (!(patchRaw ?? '').trim()) {
    return serializeProgramLearningContentFieldsForEditor(baselineFields);
  }
  const merged = mergeProgramLearningContentFields(baselineFields, {
    body: patchFields.body.trim() ? patchFields.body : baselineFields.body,
    note: patchFields.note.trim() ? patchFields.note : baselineFields.note,
    presentationRefId: patchFields.presentationRefId || baselineFields.presentationRefId,
    worksheetRefId: patchFields.worksheetRefId || baselineFields.worksheetRefId,
  });
  return serializeProgramLearningContentFieldsForEditor(merged);
}

export async function fetchProgramLearningContentFieldsForOwner(
  workspaceId: string,
  patch: { kind: ProgramLearningTextOwnerPatchKind; id: string }
): Promise<ProgramLearningContentFields | null> {
  const sid = workspaceId.trim();
  const id = patch.id.trim();
  if (!sid || !id) return null;

  const table =
    patch.kind === 'program_cell'
      ? 'program_progress_cells'
      : patch.kind === 'fixed_row'
        ? 'program_fixed_content_rows'
        : 'program_scoped_content_rows';

  const { data, error } = await supabase
    .from(table)
    .select(LEARNING_CONTENT_SELECT)
    .eq('workspace_id', sid)
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return readProgramLearningContentFields(data as ProgramLearningContentRowLike);
}

/** @deprecated editor JSON — RPC는 structured fields 사용 권장 */
export async function fetchLearningTextForProgramContentOwnerPatch(
  workspaceId: string,
  patch: { kind: ProgramLearningTextOwnerPatchKind; id: string }
): Promise<string | null> {
  const fields = await fetchProgramLearningContentFieldsForOwner(workspaceId, patch);
  if (!fields) return null;
  return serializeProgramLearningContentFieldsForEditor(fields);
}

export async function mergeLearningTextPatchWithCurrentDbBaseline(params: {
  workspaceId: string;
  kind: ProgramLearningTextOwnerPatchKind;
  id: string;
  learningText: string;
}): Promise<string> {
  const baselineFields = await fetchProgramLearningContentFieldsForOwner(params.workspaceId, {
    kind: params.kind,
    id: params.id,
  });
  if (!baselineFields) return params.learningText;
  return mergeLearningTextOwnerPatchWithSavedBaseline(params.learningText, null, baselineFields);
}

export async function mergeLearningContentPatchWithCurrentDbBaseline(params: {
  workspaceId: string;
  kind: ProgramLearningTextOwnerPatchKind;
  id: string;
  learningText: string;
}): Promise<ProgramLearningContentFields> {
  const baselineFields = await fetchProgramLearningContentFieldsForOwner(params.workspaceId, {
    kind: params.kind,
    id: params.id,
  });
  const patchFields = parseProgramLearningContentFieldsFromEditor(params.learningText);
  if (!baselineFields) return patchFields;
  return mergeProgramLearningContentFields(baselineFields, {
    body: baselineFields.body,
    note: baselineFields.note,
    presentationRefId: patchFields.presentationRefId || baselineFields.presentationRefId,
    worksheetRefId: patchFields.worksheetRefId || baselineFields.worksheetRefId,
  });
}

export function learningContentFieldsToRpcParams(fields: ProgramLearningContentFields): {
  p_learning_text: string;
  p_learning_note: string;
  p_presentation_ref_id: string | null;
  p_worksheet_ref_id: string | null;
} {
  return {
    p_learning_text: fields.body,
    p_learning_note: fields.note,
    p_presentation_ref_id: fields.presentationRefId,
    p_worksheet_ref_id: fields.worksheetRefId,
  };
}
